import { LoadingButton } from '@mui/lab';
import {
  Checkbox,
  Dialog,
  DialogContent,
  FormControlLabel,
  Icon,
  IconButton,
  Radio,
  Stack,
  Typography,
  useTheme,
} from '@mui/material';
import * as _ from 'lodash';
import { ReactElement, useCallback, useEffect, useMemo, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';

import {
  Order,
  OrderCustomFields,
  OrderStatus,
  OrderUpdate,
  OrderWorkResult,
} from '@app/types/order';

const workResults = [
  { icon: 'thumb_up', label: 'いいね', value: OrderWorkResult.GOOD },
  { icon: 'thumb_down', label: 'いまいち', value: OrderWorkResult.BAD },
] as const;

interface WorkResultModalProps {
  onClose: () => void;
  onSubmit?: (
    organizationId: string,
    orderId: string,
    payload: OrderUpdate,
    isFollow: boolean
  ) => void;
  open: boolean;
  order?: Order;
}

export function WorkResultModal({
  onClose,
  onSubmit,
  open,
  order,
}: WorkResultModalProps): ReactElement {
  const theme = useTheme();
  const [isFollow, setIsFollow] = useState<boolean>(false);
  const [isLoading, setIsLoading] = useState(false);
  const { control, formState, getValues, handleSubmit, reset } =
    useForm<OrderUpdate>({
      defaultValues: {
        customFields: order?.customFields,
        status: order?.status,
      },
      mode: 'onSubmit',
    });

  const organizationName = useMemo(() => {
    return `${
      _.get(order?.organization, 'name') || 'クリニック'
    }をフォローする`;
  }, [order?.organization]);

  const handleClose = useCallback((): void => {
    onClose();
  }, [onClose]);

  const onFormSubmit = useCallback(
    async (data: OrderUpdate) => {
      setIsLoading(true);
      const organizationId = _.get(order?.organization, 'id');
      const orderId = order?.id;
      if (!organizationId || !orderId) {
        setIsLoading(false);
        return;
      }

      const payload: OrderUpdate = {
        customFields: {
          ...(data.customFields as OrderCustomFields),
          workResultAt: new Date().toISOString(),
        },
        status: OrderStatus.PROCESSING,
      };

      if (onSubmit) {
        onSubmit(organizationId, orderId, payload, isFollow);
      }
    },
    [isFollow, onSubmit, order]
  );

  useEffect(() => {
    if (!open) {
      reset();
      setIsFollow(false);
      setIsLoading(false);
    }
  }, [open, reset]);

  const resultIcon = (icon: string, value: string): ReactElement => {
    const workResult = getValues('customFields.workResult');
    return (
      <IconButton
        sx={{
          backgroundColor:
            workResult === value
              ? 'primary.main'
              : theme.palette.neutral.greyLight,
          color:
            workResult === value
              ? theme.palette.neutral.greyLight
              : 'primary.main',
          height: 80,
          width: 80,
        }}
      >
        <Icon baseClassName="material-symbols-rounded" fontSize="large">
          {icon}
        </Icon>
      </IconButton>
    );
  };

  return (
    <Dialog open={open} onClose={handleClose}>
      <DialogContent>
        <Typography align="center">クリニックを評価してください</Typography>
        <Stack spacing={1} sx={{ mt: 2 }}>
          <Controller
            name="customFields.workResult"
            control={control}
            rules={{ required: true }}
            render={({ field }) => (
              <Stack direction="row">
                {workResults.map(({ label, value, icon }, index) => (
                  <FormControlLabel
                    key={index}
                    value={value}
                    control={
                      <Radio
                        icon={resultIcon(icon, value)}
                        checkedIcon={resultIcon(icon, value)}
                        checked={field.value === value}
                        onChange={(e) => field.onChange(e.target.value)}
                      />
                    }
                    label={
                      <Typography sx={{ fontWeight: 500 }}>{label}</Typography>
                    }
                    labelPlacement="bottom"
                  />
                ))}
              </Stack>
            )}
          />
          <FormControlLabel
            control={
              <Checkbox
                checked={isFollow}
                onChange={(e) => setIsFollow(e.target.checked)}
              />
            }
            label={
              <Typography sx={{ fontWeight: 500 }}>
                {organizationName}
              </Typography>
            }
            sx={{ justifyContent: 'center' }}
          />
          <LoadingButton
            color="black"
            variant="contained"
            loading={isLoading}
            disabled={!formState.isValid}
            fullWidth
            onClick={handleSubmit(onFormSubmit)}
          >
            送信
          </LoadingButton>
        </Stack>
      </DialogContent>
    </Dialog>
  );
}
